import { useRef, useState, useEffect } from 'react'
import { Camera } from 'lucide-react'
import { authApi, profileApi } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { useToast } from '@/stores/toast-store'
import { convertToWebP } from '@/lib/convert-to-webp'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MB

export default function ProfilePage() {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const { toast } = useToast()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [displayName, setDisplayName] = useState(user?.display_name ?? '')
  const [previewUrl, setPreviewUrl] = useState<string | null>(user?.avatar_url ?? null)
  const [webpBlob, setWebpBlob] = useState<Blob | null>(null)
  const [isConverting, setIsConverting] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'uploading' | 'saving'>('idle')

  // Sync form when user store updates (e.g. after save)
  useEffect(() => {
    setDisplayName(user?.display_name ?? '')
    if (!webpBlob) {
      setPreviewUrl(user?.avatar_url ?? null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.display_name, user?.avatar_url])

  // Revoke preview object URL on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast({ title: 'File tidak valid', description: 'Pilih file gambar (JPG, PNG, GIF, dll)', variant: 'destructive' })
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({ title: 'File terlalu besar', description: 'Ukuran maksimal adalah 5 MB', variant: 'destructive' })
      return
    }

    setIsConverting(true)
    try {
      const blob = await convertToWebP(file)
      // Revoke previous blob URL if any
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl)
      setWebpBlob(blob)
      setPreviewUrl(URL.createObjectURL(blob))
    } catch {
      toast({ title: 'Gagal memproses gambar', description: 'Coba gunakan file gambar lain', variant: 'destructive' })
    } finally {
      setIsConverting(false)
      // Reset input so the same file can be re-selected if needed
      e.target.value = ''
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      let avatarUrl = user?.avatar_url ?? null

      // Step 1: Upload gambar dulu — pastikan selesai sebelum lanjut
      if (webpBlob) {
        setSubmitStatus('uploading')
        const { url } = await profileApi.uploadAvatar(webpBlob)
        avatarUrl = url
      }

      // Step 2: Simpan profil setelah upload ImageKit selesai
      setSubmitStatus('saving')
      await profileApi.updateProfile({
        display_name: displayName.trim() || null,
        avatar_url: avatarUrl,
      })

      // Step 3: Refresh auth store agar sidebar langsung update
      const updatedUser = await authApi.me()
      setUser(updatedUser)

      // Reset pending blob — preview sekarang dari URL ImageKit
      setWebpBlob(null)

      toast({ title: 'Profil berhasil diperbarui' })
    } catch (err) {
      toast({
        title: 'Gagal memperbarui profil',
        description: (err as Error).message,
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
      setSubmitStatus('idle')
    }
  }

  const initials = ((user?.display_name ?? user?.email ?? '?')[0] ?? '?').toUpperCase()

  const submitLabel =
    submitStatus === 'uploading'
      ? 'Mengunggah foto...'
      : submitStatus === 'saving'
        ? 'Menyimpan...'
        : 'Simpan Perubahan'

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Profil</h1>
        <p className="text-sm text-muted-foreground">Perbarui nama tampilan dan foto profil kamu.</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-lg space-y-6 rounded-xl border border-border bg-card p-6">
        {/* Avatar section */}
        <div className="space-y-1.5">
          <p className="text-sm font-medium">Foto Profil</p>
          <div className="flex items-center gap-4">
            {/* Clickable avatar — triggers hidden file input */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isConverting || isSubmitting}
              className="relative h-20 w-20 shrink-0 rounded-full overflow-hidden group focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              aria-label="Ganti foto profil"
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="foto profil"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full bg-muted flex items-center justify-center text-2xl font-semibold text-muted-foreground">
                  {initials}
                </div>
              )}
              {/* Camera overlay on hover */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="h-6 w-6 text-white" />
              </div>
            </button>

            <div className="space-y-1">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isConverting || isSubmitting}
                className="text-sm font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
              >
                {isConverting ? 'Memproses...' : 'Ganti foto'}
              </button>
              <p className="text-xs text-muted-foreground">JPG, PNG, GIF, WebP · Maks. 5 MB</p>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Display name */}
        <div className="space-y-1.5">
          <label htmlFor="displayName" className="text-sm font-medium">Nama Tampilan</label>
          <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={user?.email ?? 'Nama kamu'}
            maxLength={100}
            disabled={isSubmitting}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
          <p className="text-xs text-muted-foreground">
            Nama ini akan ditampilkan kepada anggota workspace lainnya.
          </p>
        </div>

        {/* Email (read-only) */}
        <div className="space-y-1.5">
          <p className="text-sm font-medium">Email</p>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting || isConverting}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </form>
    </div>
  )
}
